import { $auditIssueFields } from '../../../../../../../../shared/constants/manual-audit-issues';
import {
  $auditFinding,
  ActiveAuditFindingQAStatus,
  AuditFindingDisabilityAffected,
  AuditFindingFunctionalImpact,
  AuditFindingIssueFrequency,
  AuditFindingQAStatus,
  AuditFindingQAStatusDisplayLabel,
  AuditFindingTaskLinkStatus,
  AuditFindingViewport,
} from '../../../../../../../../shared/constants/audit-finding';
import { filterType, IFilterOption } from '../../../../../../components/filtering/filter.interface';
import { $severity } from '../../../../../../../../shared/constants/accessibility';
import { AuditStandards } from '../../../../../../../../shared/constants/audit-standard';
import { standardNameAndLevel } from '../../../../../../../../shared/constants/scanning';
import { FINDING_TYPE_FILTER_OPTIONS } from './finding-type';

export const columnDefinitionFilterType: Readonly<Record<string, filterType>> = Object.freeze({
  [$auditIssueFields.disabilityAffected]: filterType.checkbox,
  [$auditIssueFields.conformanceLevelAndVersion]: filterType.checkbox,
  [$auditIssueFields.effort]: filterType.checkbox,
  [$auditIssueFields.severity]: filterType.checkbox,
  [$auditIssueFields.status]: filterType.checkbox,
  [$auditIssueFields.issueFrequency]: filterType.checkbox,
  [$auditIssueFields.viewport]: filterType.checkbox,
  [$auditIssueFields.task]: filterType.checkbox,
  [$auditIssueFields.isFlagged]: filterType.checkbox,
  [$auditIssueFields.findingType]: filterType.checkbox,
  [$auditIssueFields.evaluationStandard]: filterType.checkbox,

  [$auditIssueFields.issueId]: filterType.keyword,
  [$auditIssueFields.screen]: filterType.keyword,
  [$auditIssueFields.summary]: filterType.keyword,
  [$auditIssueFields.url]: filterType.keyword,
  [$auditIssueFields.instances]: filterType.keyword,
  [$auditIssueFields.actualResult]: filterType.keyword,
  [$auditIssueFields.stepsToReproduce]: filterType.keyword,
  [$auditIssueFields.recommendation]: filterType.keyword,
  [$auditIssueFields.age]: filterType.number,
  [$auditIssueFields.xpath]: filterType.keyword,
  [$auditIssueFields.codeSnippet]: filterType.keyword,

  [$auditIssueFields.successCriteria]: filterType.multiselect,
  [$auditIssueFields.criteria]: filterType.multiselect,
  [$auditIssueFields.category]: filterType.multiselect,
  [$auditIssueFields.component]: filterType.select,

  [$auditIssueFields.createdAt]: filterType.none,
  [$auditIssueFields.updatedAt]: filterType.none,
  [$auditIssueFields.actions]: filterType.none,
  [$auditIssueFields.ruleId]: filterType.none,
  [$auditFinding.screenshot]: filterType.none,

  [$auditIssueFields.matchAllAffectedDisabilities]: filterType.hidden,
  [$auditIssueFields.multipleDisabilitiesAffected]: filterType.hidden,
});

const WCAG_LEVELS_VERSIONS: IFilterOption<standardNameAndLevel>[] = [
  {
    label: 'WCAG_v20_A',
    value: standardNameAndLevel.WCAG_v20_A,
  },
  {
    label: 'WCAG_v20_AA',
    value: standardNameAndLevel.WCAG_v20_AA,
  },
  {
    label: 'WCAG_v20_AAA',
    value: standardNameAndLevel.WCAG_v20_AAA,
  },
  {
    label: 'WCAG_v21_A',
    value: standardNameAndLevel.WCAG_v21_A,
  },
  {
    label: 'WCAG_v21_AA',
    value: standardNameAndLevel.WCAG_v21_AA,
  },
  {
    label: 'WCAG_v21_AAA',
    value: standardNameAndLevel.WCAG_v21_AAA,
  },
  {
    label: 'WCAG_v22_A',
    value: standardNameAndLevel.WCAG_v22_A,
  },
  {
    label: 'WCAG_v22_AA',
    value: standardNameAndLevel.WCAG_v22_AA,
  },
  {
    label: 'WCAG_v22_AAA',
    value: standardNameAndLevel.WCAG_v22_AAA,
  },
];
export const columnCheckboxOptions = Object.freeze({
  [$auditIssueFields.disabilityAffected]: [
    {
      label: `issue_field_disability_${AuditFindingDisabilityAffected.auditory}`,
      value: AuditFindingDisabilityAffected.auditory,
    },
    {
      label: `issue_field_disability_${AuditFindingDisabilityAffected.cognitive}`,
      value: AuditFindingDisabilityAffected.cognitive,
    },
    {
      label: `issue_field_disability_${AuditFindingDisabilityAffected.neurological}`,
      value: AuditFindingDisabilityAffected.neurological,
    },
    {
      label: `issue_field_disability_${AuditFindingDisabilityAffected.physical}`,
      value: AuditFindingDisabilityAffected.physical,
    },
    {
      label: `issue_field_disability_${AuditFindingDisabilityAffected.speech}`,
      value: AuditFindingDisabilityAffected.speech,
    },
    {
      label: `issue_field_disability_${AuditFindingDisabilityAffected.visual}`,
      value: AuditFindingDisabilityAffected.visual,
    },
  ],
  [$auditIssueFields.evaluationStandard]: [
    {
      label: AuditStandards.section508,
      value: AuditStandards.section508,
    },
    {
      label: AuditStandards.en301549,
      value: AuditStandards.en301549,
    },
    ...WCAG_LEVELS_VERSIONS,
  ],
  [$auditIssueFields.conformanceLevelAndVersion]: WCAG_LEVELS_VERSIONS,
  [$auditIssueFields.effort]: [
    {
      label: 'scan_issue_severity_high',
      value: AuditFindingFunctionalImpact.high,
    },
    {
      label: 'scan_issue_severity_low',
      value: AuditFindingFunctionalImpact.low,
    },
  ],
  [$auditIssueFields.severity]: [
    {
      label: 'scan_issue_severity_critical',
      value: $severity.critical,
    },
    {
      label: 'scan_issue_severity_high',
      value: $severity.high,
    },
    {
      label: 'scan_issue_severity_low',
      value: $severity.low,
    },
  ],
  [$auditIssueFields.status]: ActiveAuditFindingQAStatus.map((status: AuditFindingQAStatus): IFilterOption<any> => {
    return {
      label: AuditFindingQAStatusDisplayLabel[status],
      value: status,
    };
  }),
  [$auditIssueFields.issueFrequency]: [
    {
      label: 'issue_field_frequency_individual',
      value: AuditFindingIssueFrequency.individual,
    },
    {
      label: 'issue_field_frequency_global',
      value: AuditFindingIssueFrequency.global,
    },
  ],
  [$auditIssueFields.task]: [
    {
      label: 'issue_field_task_linked',
      value: AuditFindingTaskLinkStatus.linked,
    },
    {
      label: 'issue_field_task_unlinked',
      value: AuditFindingTaskLinkStatus.unlinked,
    },
  ],
  [$auditIssueFields.isFlagged]: [
    {
      label: 'flagged',
      value: 'true',
    },
    {
      label: 'not_flagged',
      value: 'false',
    },
  ],
  [$auditIssueFields.findingType]: FINDING_TYPE_FILTER_OPTIONS,
  [$auditIssueFields.viewport]: [
    {
      label: 'issue_field_viewport_small',
      value: AuditFindingViewport.small,
    },
    {
      label: 'issue_field_viewport_medium',
      value: AuditFindingViewport.medium,
    },
    {
      label: 'issue_field_viewport_large',
      value: AuditFindingViewport.large,
    },
  ],
});
